import React from 'react';
import { MDXRenderer } from 'gatsby-plugin-mdx';
import { MDXProvider } from '@mdx-js/react';
import { IconFileLoadDownloadArrow } from '../Icons';

import mcu from './images/mcu.svg';
import core from './images/core.svg';
import communication from './images/communication.svg';
import connection from './images/connection.svg';
import configurability from './images/configurability.svg';
import camera from './images/camera.svg';
import colorSensor from './images/color-sensor.svg';
import cryptoChip from './images/crypto-chip.svg';
import humiditySensor from './images/humidity-sensor.svg';
import imu from './images/imu.svg';
import lightSensor from './images/light-sensor.svg';
import microphone from './images/microphone.svg';
import magnetometer from './images/magnetometer.svg';
import fileIcon from './images/file-icon.svg';
import location from './images/location.svg';
import worldMap from './images/world-map.svg';
import hwPin from './images/hw-pin.svg';
import led from './images/led.svg';

import niclaFormFactor from './images/nicla-form-factor.svg';
import nanoFormFactor from './images/nano-form-factor.svg';
import mkrFormFactor from './images/mkr-form-factor.svg';
import unoFormFactor from './images/uno-form-factor.svg';
import megaFormFactor from './images/mega-form-factor.svg';

import python from './images/python.svg';
import pressureSensor from './images/pressure-sensor.svg';
import proximitySensor from './images/proximity-sensor.svg';
import simCard from './images/sim-card.svg';
import temperatureSensor from './images/temperature-sensor.svg';
import uvSensor from './images/pressure-sensor.svg';
import wifi from './images/wifi.svg';
import bluetooth from './images/bluetooth.svg';
import usb from './images/usb.svg';
import power from './images/power.svg';
import cellular from './images/cellular.svg';
import defaultFeature from './images/defaultFeature.svg';

import './ProductFeatures.scss';

interface FeatureLinkProps {
  title: string;
  url: string;
  blank: boolean;
  download: boolean;
}

export function FeatureLink(props: FeatureLinkProps): React.ReactElement {
  const { title = 'Learn More', url, blank = false, download = false } = props;
  const aRefProps = blank ? { target: '_blank', rel: 'noopener noreferrer' } : {};
  return (
    <div className="product-features__link">
      <a href={url} {...aRefProps}>
        {title}
        {download && <IconFileLoadDownloadArrow />}
      </a>
    </div>
  );
}

interface FeatureProps {
  title: string;
  image: string;
  children: React.ReactNode;
}

interface FeatureWrapperProps {
  children: React.ReactNode | Array<React.ReactNode> | any;
}

export function FeatureWrapper(props: FeatureWrapperProps): React.ReactElement {
  const { children } = props;

  if (!children.length) {
    return <div className="product-features__single-link">{children}</div>;
  } else {
    return <div className="product-features__wrapper">{children}</div>;
  }
}

export function Feature(props: FeatureProps): React.ReactElement {
  const { title, image, children } = props;
  const getImage = (image) => {
    switch (image) {
      case 'hw-pin':
        return <img className="product-features__feature--image" src={hwPin} alt="Pin" />;
      case 'led':
        return <img className="product-features__feature--image" src={led} alt="LED" />;
      case 'file-icon':
        return <img className="product-features__feature--image" src={fileIcon} alt="File" />;
      case 'location':
        return <img className="product-features__feature--image" src={location} alt="Location" />;
      case 'world-map':
        return <img className="product-features__feature--image" src={worldMap} alt="World Map" />;
      case 'python':
        return <img className="product-features__feature--image" src={python} alt="Python" />;
      case 'mcu':
        return <img className="product-features__feature--image" src={mcu} alt="Microcontroller" />;
      case 'core':
        return <img className="product-features__feature--image" src={core} alt="Core" />;
      case 'wifi':
        return <img className="product-features__feature--image" src={wifi} alt="Wi-Fi" />;
      case 'bluetooth':
        return <img className="product-features__feature--image" src={bluetooth} alt="Bluetooth®" />;
      case 'usb':
        return <img className="product-features__feature--image" src={usb} alt="USB" />;
      case 'power':
        return <img className="product-features__feature--image" src={power} alt="Power" />;
      case 'cellular':
        return <img className="product-features__feature--image" src={cellular} alt="Cellular" />;
      case 'communication':
        return <img className="product-features__feature--image" src={communication} alt="Communication" />;
      case 'connection':
        return <img className="product-features__feature--image" src={connection} alt="Connection" />;
      case 'configurability':
        return <img className="product-features__feature--image" src={configurability} alt="Configurability" />;
      case 'camera':
        return <img className="product-features__feature--image" src={camera} alt="Camera" />;
      case 'color-sensor':
        return <img className="product-features__feature--image" src={colorSensor} alt="Color Sensor" />;
      case 'crypto-chip':
        return <img className="product-features__feature--image" src={cryptoChip} alt="Crypto Chip" />;
      case 'humidity-sensor':
        return <img className="product-features__feature--image" src={humiditySensor} alt="Humidity Sensor" />;
      case 'imu':
        return <img className="product-features__feature--image" src={imu} alt="IMU" />;
      case 'light-sensor':
        return <img className="product-features__feature--image" src={lightSensor} alt="Light Sensor" />;
      case 'microphone':
        return <img className="product-features__feature--image" src={microphone} alt="Microphone" />;
      case 'magnetometer':
        return <img className="product-features__feature--image" src={magnetometer} alt="Magnetometer" />;
      case 'nicla-form-factor':
        return <img className="product-features__feature--image" src={niclaFormFactor} alt="Nicla Form Factor" />;
      case 'nano-form-factor':
        return <img className="product-features__feature--image" src={nanoFormFactor} alt="Nano Form Factor" />;
      case 'mkr-form-factor':
        return <img className="product-features__feature--image" src={mkrFormFactor} alt="MKR Form Factor" />;
      case 'uno-form-factor':
        return <img className="product-features__feature--image" src={unoFormFactor} alt="UNO Form Factor" />;
      case 'mega-form-factor':
        return <img className="product-features__feature--image" src={megaFormFactor} alt="Mega Form Factor" />;
      case 'pressure-sensor':
        return <img className="product-features__feature--image" src={pressureSensor} alt="Pressure Sensor" />;
      case 'proximity-sensor':
        return <img className="product-features__feature--image" src={proximitySensor} alt="Proximity Sensor" />;
      case 'sim-card':
        return <img className="product-features__feature--image" src={simCard} alt="SIM Card" />;
      case 'temperature-sensor':
        return <img className="product-features__feature--image" src={temperatureSensor} alt="Temperature Sensor" />;
      case 'uv-sensor':
        return <img className="product-features__feature--image" src={uvSensor} alt="UV Sensor" />;
      case 'wifi-bluetooth':
        return (
          <div>
            <img className="product-features__feature--image" src={wifi} alt="WiFi and Bluetooth" />
            <img className="product-features__feature--image" src={bluetooth} alt="WiFi and Bluetooth" />
          </div>
        );
      default:
        return <img className="product-features__feature--imageDefault" src={defaultFeature} alt="Feature" />;
    }
  };

  return (
    <div className="product-features__feature">
      {getImage(image)}
      <div>
        <div className="product-features__feature--title">{title}</div>
        <div className="product-features__feature--content">{children}</div>
      </div>
    </div>
  );
}

interface FeatureListProps {
  children: any;
}

export function FeatureList(props: FeatureListProps): React.ReactElement {
  return <div className="product-features--item">{props.children}</div>;
}
interface FeatureDescriptionProps {
  children: React.ReactNode;
}

export function FeatureDescription(props: FeatureDescriptionProps): React.ReactElement {
  return <div className="product-features__description">{props.children}</div>;
}

interface ProductFeaturesProps {
  features: {
    body: string;
  };
}

export default function ProductFeatures({ features }: ProductFeaturesProps): React.ReactElement {
  return (
    <div className="product-features">
      <MDXProvider
        components={{
          FeatureDescription,
          FeatureList,
          Feature,
          FeatureLink,
          FeatureWrapper,
        }}
      >
        {features && <MDXRenderer>{features.body}</MDXRenderer>}
      </MDXProvider>
    </div>
  );
}
